import { format, getHours, isSameDay, parse } from "date-fns";
import { ChartDataLabel } from "locale/es";
import moment from "moment";
import { AppColors } from "resources/AppColors";

export function secondsToMinutes(seconds) {
  return Math.floor(seconds / 60);
}

export function ageFromBirthdate(birthDay) {
  if (!birthDay) return

  const today = new Date();
  const birthDate = new Date(birthDay);

  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  return age;
}

export const isEmptyObj = (obj) => {
  if (obj) {
    return Object.keys(obj).length === 0;
  }
  else return true
}

export const formatDate = (isoDate) => {
  if (isoDate) {
    const formattedDate = new Date(isoDate).toLocaleDateString('es-ES')
    return formattedDate
  }
  return ''
}

export const formatDateWithHourAndMinute = (isoDate) => {
  if (isoDate) {
    // ! Check how the format comes from the BE
    const parsedDate = parse(isoDate, "yyyy-MM-dd'T'HH:mm:ssX", new Date());
    const formattedDate = format(parsedDate, "dd-MM-yyyy HH:mm");
    return formattedDate
  }
  return ''
}

export const convertToLocalTime = (data) => {
  return data.map(dataPoint => {
    return { ...dataPoint, timestamp: new Date(dataPoint.timestamp) }
  })
}

export const getWeekDaysByWeekNumber = (selectedDate) => {
  const dateFormat = "DD/MM/YYYY";
  moment.updateLocale('es-ES', {
    week: {
      dow: 1
    }
  });

  const weekNumber = moment(selectedDate).isoWeek();

  const date = moment().isoWeekYear(moment(selectedDate).format('GGGG')).isoWeek(weekNumber || 1).startOf("week")

  let weekLength = 7
  let result = []

  while (weekLength--) {
    result.push(date.format(dateFormat));
    date.add(1, "day")
  }

  return result;
}

export const getDaysArrayByMonth = (selectedDate) => {
  const dateFormat = "DD/MM/YYYY";

  let daysInMonth = moment(selectedDate).daysInMonth();
  let arrDays = [];

  while (daysInMonth) {
    const current = moment(selectedDate).date(daysInMonth).format(dateFormat);
    arrDays.push(current);
    daysInMonth--;
  }

  return arrDays.reverse()
}

export const getDaysBySelectedRange = (startDate, endDate) => {
  const dateFormat = "DD/MM/YYYY";

  let totalDays = moment(endDate).diff(moment(startDate), 'days') + 1

  let arrDays = [];

  let dateReference = moment(endDate).clone()

  while (totalDays) {
    const current = moment(dateReference).format(dateFormat);
    arrDays.push(current);
    dateReference.subtract(1, 'days')
    totalDays--;
  }

  return arrDays.reverse()
}

// TODO: Modify this function to get days between the relevant range instead of a whole month
export const getMonthWeeksArray = (startDate, endDate) => {
  const dateFormat = "DD/MM/YYYY";
  moment.updateLocale('es-ES', {
    week: {
      dow: 1
    }
  });

  const days = getDaysBySelectedRange(startDate, endDate)

  const weeksRangeArray = days.reduce((weeksRange, currentDay) => {
    const weekDayNumber = moment(currentDay, dateFormat).isoWeekday()
    const lastDay = days[days.length - 1]

    if (weekDayNumber === 1) {
      weeksRange = [...weeksRange, weeksRange[weeksRange.length] = [currentDay]]
    }

    if (weekDayNumber === 7) {
      weeksRange[weeksRange.length - 1][1] = currentDay
    }

    if (currentDay === lastDay) {
      weeksRange[weeksRange.length - 1][1] = currentDay
    }

    return weeksRange
  }, [])

  return weeksRangeArray
}

export const getHoursArray = () => {
  let hours = 24
  let hoursArray = []

  for (let n = 0; n < hours; n++) {
    hoursArray.push(n.toString())
  }

  return hoursArray
}

export const formatDateForRequests = (date) => {
  return date.format().toString().split("+")[0] + "Z"
}

// TODO: Separate filter and sorting into individual functions
// ! sort mutates the original data
export const filterAndSortDates = (data, selectedDate, dateRange) => {
  moment.updateLocale('es-ES', {
    week: {
      dow: 1
    }
  });

  // TODO: Replace isSame method as it is prone to errors
  return data.filter(dataPoint => moment(dataPoint.timestamp).isSame(selectedDate, dateRange))
    .sort((a, b) =>
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    )
}

export const filterAndSortSameDayFNS = (data, selectedDate) => {
  return data.filter(dataPoint => isSameDay(new Date(dataPoint.timestamp), new Date(selectedDate)))
    .sort((a, b) =>
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    )
}

// TODO: Separate filter and sorting into individual functions
export const filterAndSortSpecificDates = (data, startDate, endDate) => {
  moment.updateLocale('es-ES', {
    week: {
      dow: 1
    }
  });

  return [...data].filter(dataPoint => moment(dataPoint.timestamp).isBetween(startDate, endDate, 'day', '[]'))
    .sort((a, b) =>
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    )
}

export const getMaxValuesPerHour = (calendar, data) => {
  return calendar.reduce((timeUnitDict, timeUnit) => {
    let parsedTimeUnits = []

    data.forEach(dataPoint => {
      const formattedTimestamp = getHours(new Date(dataPoint.timestamp)).toString()

      if (timeUnit === formattedTimestamp) {
        parsedTimeUnits.push(formattedTimestamp)

        timeUnitDict = {
          ...timeUnitDict,
          [timeUnit]: {
            // TODO: Simplify this nested mess
            maxValue: timeUnitDict[timeUnit]?.maxValue ?
              parseInt(timeUnitDict[timeUnit].maxValue) < parseInt(dataPoint.value) ? parseInt(dataPoint.value)
                : parseInt(timeUnitDict[timeUnit].maxValue)
              : parseInt(dataPoint.value),
          }
        }
      }
    })
    if (!parsedTimeUnits.includes(timeUnit)) {
      timeUnitDict = {
        ...timeUnitDict,
        [timeUnit]: {
          maxValue: 0,
        }
      }
    }
    return timeUnitDict
  }, {})
}

export const getMinAndMaxValuesPerTimeUnit = (calendar, data, dateFormat) => {
  moment.updateLocale('es-ES', {
    week: {
      dow: 1
    }
  });

  return calendar.reduce((timeUnitDict, timeUnit) => {
    let parsedTimeUnits = []

    data.forEach(dataPoint => {
      const formattedTimestamp = moment.parseZone(dataPoint.timestamp).format(dateFormat)

      if (timeUnit === formattedTimestamp) {
        parsedTimeUnits.push(formattedTimestamp)

        timeUnitDict = {
          ...timeUnitDict,
          [timeUnit]: {
            // TODO: Simplify this nested mess
            minValue: timeUnitDict[timeUnit]?.minValue ?
              parseInt(timeUnitDict[timeUnit].minValue) > parseInt(dataPoint.value) ? parseInt(dataPoint.value)
                : parseInt(timeUnitDict[timeUnit].minValue)
              : parseInt(dataPoint.value),
            maxValue: timeUnitDict[timeUnit]?.maxValue ?
              parseInt(timeUnitDict[timeUnit].maxValue) < parseInt(dataPoint.value) ? parseInt(dataPoint.value)
                : parseInt(timeUnitDict[timeUnit].maxValue)
              : parseInt(dataPoint.value),
          }
        }
      }
    })
    if (!parsedTimeUnits.includes(timeUnit)) {
      timeUnitDict = {
        ...timeUnitDict,
        [timeUnit]: {
          minValue: 0,
          maxValue: 0,
        }
      }
    }
    return timeUnitDict
  }, {})
}

export const getTotalValuesPerHour = (calendar, data, dateFormat) => {
  moment.updateLocale('es-ES', {
    week: {
      dow: 1
    }
  });

  return calendar.reduce((timeUnitDict, timeUnit) => {
    let parsedTimeUnits = []

    data.forEach(dataPoint => {
      const formattedTimestamp = moment(dataPoint.timestamp).format(dateFormat)

      if (timeUnit === formattedTimestamp) {
        parsedTimeUnits.push(moment(dataPoint.timestamp).format(dateFormat))
        timeUnitDict = {
          ...timeUnitDict,
          [timeUnit]: (parseInt(timeUnitDict[timeUnit] || 0) + parseInt(dataPoint.value))
        }
      }
    })
    if (!parsedTimeUnits.includes(timeUnit)) {
      timeUnitDict = {
        ...timeUnitDict,
        [timeUnit]: 0
      }
    }
    return timeUnitDict
  }, {})
}

export const createDayAndValuesDict = (calendar, data, dateFormat) => {
  return calendar.reduce((timeUnitDict, timeUnit) => {
    let parsedTimeUnits = []

    data.forEach(dataPoint => {
      const formattedTimestamp = moment.parseZone(dataPoint.timestamp).format(dateFormat)

      if (timeUnit === formattedTimestamp) {
        parsedTimeUnits.push(formattedTimestamp)
        timeUnitDict = {
          ...timeUnitDict,
          [timeUnit]: (parseInt(timeUnitDict[timeUnit]?.value || 0) + parseInt(dataPoint.value))
        }
      }
    })
    if (!parsedTimeUnits.includes(timeUnit)) {
      timeUnitDict = {
        ...timeUnitDict,
        [timeUnit]: 0
      }
    }
    return timeUnitDict
  }, {})
}

export const getTotalValuesPerWeek = (timeUnitDict, calendar, dateFormat) => {
  moment.updateLocale('es-ES', {
    week: {
      dow: 1
    }
  });

  return Object.entries(timeUnitDict).reduce((perWeekDict, timeUnitAndValue) => {
    const [timeUnit, value] = timeUnitAndValue

    calendar.forEach((weekRange) => {
      if (moment(timeUnit, dateFormat).isBetween(moment(weekRange[0], dateFormat), moment(weekRange[1], dateFormat), 'days', '[]')) {
        perWeekDict = {
          ...perWeekDict,
          [weekRange]: (parseInt(perWeekDict[weekRange]) || 0) + parseInt(value)
        }
      }
    })
    return perWeekDict
  }, {})
}

export const createMinAndMaxLineChartObject = (minValues, maxValues, labels) => {
  return {
    data1: {
      label: ChartDataLabel.MIN_HEART_RATE,
      data: minValues,
      backgroundColor: AppColors.GREY_CHART,
      borderColor: AppColors.GREY_CHART,
    },
    data2: {
      label: ChartDataLabel.MAX_HEART_RATE,
      data: maxValues,
      backgroundColor: AppColors.RED_CHART,
      borderColor: AppColors.RED_CHART,
    },
    labels,
  }
}

export const createBarChartObject = (values, dataLabel, labels) => {
  return {
    data1: {
      label: dataLabel,
      data: values,
      backgroundColor: AppColors.BLUE_CHART,
      borderColor: AppColors.BLUE_CHART,
    },
    labels,
  }
}

export const dateIsValid = (date) => date instanceof Date && !isNaN(date)

// ! Deprecated: Will be replaced by appendFullNames2 after mapping data in all of the reducers that deal with people's names
export const appendFullNames = (personsArray) => {
  return personsArray.map((person) => {
    if (!person) {
      return
    }
    if (person.user?.lastName === "") {
      person = { ...person, id: person.id, fullName: person.user?.firstName }
    }
    else {
      person = { ...person, id: person.id, fullName: `${person.user?.firstName} ${person.user?.lastName}` }
    }
    return person
  })
}

// * For objects with relatedTo property instead of user like on the method above
export const appendFullNames2 = (entities) => {
  return entities.map((entity) => {
    if (!entity) {
      return
    }
    if (entity.lastName === "") {
      entity = { ...entity, id: entity.id, fullName: entity.firstName }
    }
    else {
      entity = { ...entity, id: entity.id, fullName: `${entity.firstName} ${entity.lastName}` }
    }
    return entity
  })
}

export function encodeImageFileAsURL(file, cb) {
  if (!file) return null

  let reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onloadend = function () {
    cb(reader.result)
  }
  reader.onerror = function (error) {
    console.error('File Reader Error: ', error);
  };
  return reader
}

export function validFileSize(file) {
  const maxSize = 3000000;

  if (!file.size) return false

  if (file.size > maxSize) {
    return false
  }
  return true
}

export const createEmptyStateLexical = () => '{"root":{"children":[{"children":[],"direction":null,"format":"","indent":0,"type":"paragraph","version":1}],"direction":null,"format":"","indent":0,"type":"root","version":1}}'
export const createEmptyStateLexicalNotificationText = () => '{"root":{"children":[{"children":[],"direction":null,"format":"","indent":0,"type":"paragraph","version":1}],"direction":null,"format":"","indent":0,"type":"root","version":1}}'
